import { CommonModule } from '@angular/common';
import { Component, forwardRef, Inject, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { SelectionConfig } from 'src/app/shared/class/Select';
import { ISelection } from 'src/app/shared/interfaces/ISelect';
import { IRetrieveParam } from 'src/app/shared/interfaces/ITable';
import { IconModule } from 'src/app/icon/icon.module';

@Component({
  selector: 'app-form-select',
  standalone: true,
  imports: [MaterialModule, CommonModule, TranslateModule, IconModule],
  templateUrl: './form-select.component.html',
  styleUrl: './form-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormSelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormSelectComponent),
      multi: true
    }
  ]
})
export class FormSelectComponent implements ControlValueAccessor, Validator, OnChanges, OnInit {
  @ViewChild(MatSelect, { static: false }) matSelect!: MatSelect;
  @Input() fetchData?: (params: IRetrieveParam) => Observable<any>;
  @Input() mapApiResponse?: (response: any) => any;
  @Input() appearance?: 'outline' | 'fill';
  @Input() dataSource: ISelection[] = [];
  @Input() params: IRetrieveParam = { page: 1, pageSize: 1000, sortText: '', globalSearch: '' };
  @Input() selectionConfig: SelectionConfig;
  @Input() placeholder = '';
  @Input() onOpen?: () => void;
  @Input() valueChanged?: (value: any) => void; // callback for standalone use (no form control)
  @Input() readonly = false;
  @Input() set selectedValue(val: any) { this.writeValue(val); }
  formControl: FormControl;
  value: any;
  disabled = false;
  unsubscribe: Subscription;
  groups: string[] = [];
  loading = false;
  control: AbstractControl;
  isRequired = false;
  isOpen = false;
  // Angular form callback function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => { };
  onTouched = () => { };

  constructor(@Inject(Injector) private injector: Injector) { }

  ngOnInit(): void {
    this.setFormControl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fetchData']) {
      this.retrieveData();
    }
    if (changes['dataSource']) {
      this.dataSource = this.sortByText(this.dataSource);
      this.groups = this.getGroup(this.dataSource);
    }
  }

  writeValue(value: any): void {
    if (value == null) {
      this.value = null;
      return;
    }
    if (value) {
      switch (typeof value) {
        case 'object': {
          // for default value is array, use with multiple selected
          this.value = value?.length > 0 ? value.map((e: any) => e[this.selectionConfig.key ?? ''] ?? e) : [];
          break;
        }
        default: {
          // for default value is guid and number
          this.value = value;
          break;
        }
      }
      if (this.value != value&& this.control) {
        // handle form value
        this.control.setValue(this.value);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    this.isRequired = control.hasValidator(Validators.required) || false; // check form required
    if (this.isRequired) {
      return (this.value ?? '').toString().length > 0 ? null : { required: true };
    }
    return null;
  }

  onSelectChange(value: any): void {
    if (this.readonly) return;
    if (this.selectionConfig?.isMultiSelect && Array.isArray(value) && !value.length) value = null;
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.valueChanged?.(value);
  }

  onSelectOpened(isOpen: boolean): void {
    this.isOpen = isOpen;
    if (isOpen && this.onOpen) {
      this.onOpen();
    }
  }

  retrieveData() {
    if (this.fetchData) {
      const reqParams = { ...this.params };
      this.dataSource = [];
      this.unsubscribe?.unsubscribe();
      this.loading = true;
      this.unsubscribe = this.fetchData(reqParams).subscribe((response) => {
        let dataMapApi;
        if (this.mapApiResponse) {
          dataMapApi = this.mapApiResponse(response.data ?? response);
        }
        this.dataSource = this.sortByText((dataMapApi ?? response.data ?? response).map((e: any) => ({
          value: e[this.selectionConfig.key || ''],
          text: e[this.selectionConfig.title || ''],
          group: e[this.selectionConfig.group || ''],
          disabled:
            typeof this.selectionConfig?.disabled === 'boolean'
              ? this.selectionConfig?.disabled
              : this.selectionConfig.disabled && this.selectionConfig.disabled(e)
        })));
        this.groups = this.getGroup(this.dataSource);
        this.loading = false;
      });
    }
  }

  getGroup(data: any): string[] {
    return data.reduce((result: string[], currentValue: ISelection) => {
      if (currentValue?.group && !result.includes(currentValue?.group)) {
        result.push(currentValue.group);
      }
      return result;
    }, []);
  }

  getGroupData(group: string): ISelection[] {
    return this.dataSource.filter((item: ISelection) => item.group === group);
  }

  sortByText(options: ISelection[]): ISelection[] {
    return [...options].sort((a, b) => (a.text ?? '').localeCompare(b.text ?? '', undefined, { sensitivity: 'accent' }));
  }

  hasError(): boolean {
    return this?.control?.invalid && this?.control?.touched;
  }

  isSelectDisabled(): boolean {
    return (typeof this.selectionConfig?.disabled === 'boolean' && this.selectionConfig.disabled) || this.disabled || this.readonly;
  }

  setFormControl = () => {
    try {
      const formControl = this.injector.get(NgControl, null);
      if (!formControl)
        return;
      switch (formControl.constructor) {
        case FormControlName:
          this.control = this.injector.get(FormGroupDirective).getControl(formControl as FormControlName);
          break;
        default:
          this.control = (formControl as FormControlDirective).form as FormControl;
          break;
      }
    } catch (err) {
      console.error(err);
      // this.control = new FormControl();
    }
  };

  clearInput(event?: Event) {
    if (this.readonly) return;

    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    this.value = null;
    if (this.control) {
      this.control.setValue(null);
      this.control.markAsTouched();
    }
    this.onChange(null);
    this.onTouched();
    this.valueChanged?.(null);
  }

  closeDropdown(): void {
    if (this.matSelect && this.matSelect.panelOpen) {
      this.matSelect.close();
    }
  }
}
